/** Erro já traduzido do envelope do BFF (spec §5.2). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly errors: ApiErrorDetail[],
  ) {
    super(errors[0]?.message ?? 'Erro inesperado.')
    this.name = 'ApiError'
  }

  get code(): string {
    return this.errors[0]?.code ?? 'PORTAL_INTERNAL'
  }

  /** `existingTransferId` do 409 duplicado, quando houver. */
  get existingTransferId(): string | undefined {
    return this.errors[0]?.meta?.existingTransferId as string | undefined
  }
}

export type ApiErrorDetail = {
  code: string
  message: string
  field: string | null
  meta?: Record<string, unknown>
}

type RequestOptions = {
  method?: 'GET' | 'POST'
  body?: unknown
  idempotencyKey?: string
  signal?: AbortSignal
}

/**
 * Avisado quando o BFF devolve 401 fora das rotas de login.
 *
 * A SPA registra aqui a limpeza do cache e a ida para `/login?expired=1`; o
 * cliente não importa o router para não criar dependência circular.
 */
let onSessionExpired: (() => void) | null = null

export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'

  const headers: Record<string, string> = { accept: 'application/json' }

  // Header customizado obrigatório em toda escrita: um form HTML de outro site
  // não consegue enviá-lo (spec §8).
  if (method !== 'GET') headers['x-tp-request'] = '1'
  if (options.body !== undefined) headers['content-type'] = 'application/json'
  if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey

  const response = await fetch(path, {
    method,
    headers,
    // O cookie é `SameSite=Strict` e a origem é a mesma; `same-origin` deixa
    // isso explícito.
    credentials: 'same-origin',
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  })

  if (response.status === 204) return undefined as T

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    const errors: ApiErrorDetail[] = payload?.errors ?? [
      { code: 'PORTAL_INTERNAL', message: 'Erro inesperado.', field: null },
    ]

    if (response.status === 401 && !path.startsWith('/api/auth/')) onSessionExpired?.()

    throw new ApiError(response.status, errors)
  }

  return payload as T
}
